"use client";

import CustomButton from "@/components/ui/custom/CustomButton";
import { Button as ShadButton } from "@/components/ui/shadcn/button";
import { gsap } from "@/lib/gsap";
import { PageSection } from "@/@types/site";

import { formatBoxOfficeLink, LinkService } from "@/lib/navigation";
import { cn, handleScrollToId } from "@/lib/utils";
import { Menu, X, Sparkles } from "lucide-react";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

interface HeaderProps {
  hideNav?: boolean;
  sections?: PageSection[];
  ticketRef?: string;
  showSocialLinks?: boolean;
}

/**
 * Main header component for all pages
 * Accepts sections prop to customize navigation
 *
 * @param hideNav - Hide all navigation (just show logo and Buy Tickets)
 * @param sections - Array of page sections for navigation
 * @param ticketRef - Referral code for ticket links
 * @param showSocialLinks - Show social media links (default: true)
 */
export default function Header({
  hideNav = false,
  sections = [],
  ticketRef = "atc-website-nav",
  showSocialLinks = true,
}: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const socialLinks = LinkService.getSocialLinks();

  useEffect(() => {
    // Animate the button (delayed to start after overlay)
    gsap.to(headerRef.current, {
      opacity: 1,
      duration: 0.8,
      delay: 1.5,
      ease: "power2.inOut",
    });

    return () => {};
  }, []);
  return (
    <>
      {/* Backdrop for mobile menu */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-10 transition-opacity duration-300"
          onClick={() => setIsOpen(false)} // Close menu on backdrop click
          aria-hidden="true"
        />
      )}
      <div
        ref={headerRef}
        id="header"
        className="py-4 sm:py-6 px-6 lg:px-20 flex justify-center fixed w-full top-0 left-0 bg-white backdrop-blur-sm border-b border-border z-20"
      >
        <nav className="max-w-[1000px] mx-auto sm:mx-4 w-full flex items-center justify-between">
          {/* Logo */}
          <Link
            href="/"
            className="flex items-center pl-4 lg:pl-6"
            onClick={() => {
              // scrollToId('hero');
              handleScrollToId("hero");
              setIsOpen(false);
            }}
            title="Home"
          >
            <div className="relative h-12 w-[75px]">
              {/* TODO: dark mode logo image `className='dark:bg-white dark:object-cover dark:rounded-full dark:p-2'`*/}
              {/* Define dimensions for Image */}
              <OptimizedImage
                className="mr-0 lg:mr-2 navbar-logo"
                src="/images/logo/atc-logo-full-transparent.png"
                alt="Acquired Taste Comedy Logo"
                fill
                loading="eager"
                style={{ objectFit: "contain" }} // Ensures aspect ratio
              />
            </div>
          </Link>

          {/* Buy Tickets Button (when hideNav is true) */}
          {hideNav && (
            <CustomButton
              isBlank={true}
              href={formatBoxOfficeLink(ticketRef)}
              text="Buy Tickets"
              className="block"
              showArrow={true}
            />
          )}

          {/* Hamburger Button (Mobile) */}
          {!hideNav && (
            <ShadButton
              variant="ghost"
              size="icon"
              className="lg:hidden focus:outline-none"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle mobile menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </ShadButton>
          )}

          {/* Navigation (Desktop + Mobile Menu) */}
          {!hideNav && (
            <div
              className={cn(
                "items-center grow justify-between lg:flex",
                isOpen
                  ? "block absolute top-full right-0 px-12 z-20 bg-background/95 backdrop-blur-sm border-b border-border w-full transition-all duration-300"
                  : "hidden lg:block lg:static lg:shadow-none lg:w-auto",
              )}
            >
              <div className="flex justify-between w-full items-center flex-col lg:flex-row">
                {/* Nav Links */}
                {sections.length > 0 && (
                  <ul className="list-none p-0 m-0 flex items-center flex-col lg:flex-row cursor-pointer">
                    {sections.map((section) => {
                      const isExternal = section.href && !section.href.startsWith("/");
                      return (
                        <li key={section.id}>
                          {section.href ? (
                            // External or different page link
                            <Link
                              href={section.href}
                              target={isExternal ? "_blank" : undefined}
                              rel={isExternal ? "noopener noreferrer" : undefined}
                              onClick={() => setIsOpen(false)}
                              className="flex m-0 px-0 py-4 text-foreground font-medium leading-normal lg:ml-8 hover:text-primary hover:underline items-center gap-1"
                            >
                              <span className={section.hasWow ? "mr-1" : ""}>
                                {section.title}
                              </span>
                              {section.hasWow && (
                                <sup className="text-red-500 flex items-center justify-center">
                                  <Sparkles className="w-3 h-3" size={20} strokeWidth={2} />
                                </sup>
                              )}
                            </Link>
                          ) : (
                            // Scroll-to anchor link
                            <span
                              data-slug={section.id}
                              onClick={() => {
                                handleScrollToId(section.id);
                                setIsOpen(false);
                              }}
                              className="flex m-0 px-0 py-4 text-foreground font-medium leading-normal lg:ml-8 hover:text-primary hover:underline cursor-pointer items-center gap-1"
                            >
                              <span className={section.hasWow ? "mr-1" : ""}>
                                {section.title}
                              </span>
                              {section.hasWow && (
                                <sup className="text-red-500 flex items-center justify-center">
                                  <Sparkles className="w-3 h-3" size={20} strokeWidth={2} />
                                </sup>
                              )}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {/* Social Links (Mobile Only) and Buy Tickets (always right-aligned) */}
                {/* Right-aligned: Buy Tickets and Social Links (row on desktop, stacked on mobile) */}
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-4 lg:ml-auto mt-4 lg:mt-0">
                  {/* Mobile: social links row */}
                  {showSocialLinks && (
                    <div
                      id="mobile-social-links-row"
                      className="lg:hidden flex items-center justify-center gap-4 text-primary border-t border-muted pt-4 mb-2 w-full"
                    >
                      {socialLinks.map((link) => (
                        <a
                          key={link.slug}
                          href={link.slug}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-full border-0 font-light leading-tight hover:bg-muted"
                          onClick={() => setIsOpen(false)}
                        >
                          <link.icon className="h-5 w-5" />
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Buy Tickets button (always present) */}
                  <div className="flex justify-center items-center lg:justify-end py-4 lg:py-0 w-full lg:w-auto">
                    <CustomButton
                      isBlank={true}
                      href={formatBoxOfficeLink(ticketRef)}
                      text="Buy Tickets"
                      showArrow={false}
                      onClick={() => setIsOpen(false)}
                      className="w-[260px] lg:w-auto"
                    />
                  </div>
                  {/* Desktop: social links row */}
                  {showSocialLinks && (
                    <div className="hidden lg:flex items-center justify-center gap-2 text-primary">
                      {socialLinks
                        .filter((link) => link.desktopVisible)
                        .map((link) => (
                          <a
                            key={link.slug}
                            href={link.slug}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-full border-0 font-light leading-tight hover:bg-muted"
                            onClick={() => setIsOpen(false)}
                          >
                            <link.icon className="h-5 w-5" />
                          </a>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </div>
    </>
  );
}

// Optional CSS for active link styling (since scoped SCSS isn't directly supported)
export const headerStyles = `

`;
